/* eslint-disable spire/export-styles */
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { canAddToCart, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import Icon from "@insite/mobius/Icon";
import TextField, { TextFieldPresentationProps, TextFieldProps } from "@insite/mobius/TextField";
import Typography from "@insite/mobius/Typography";
import cenQtyMin from "@tCentro/Handlers/CenQtyMinHandler";
import cenQtyMulti from "@tCentro/Handlers/CenQtyMultiHandler";
import CENErrorIcon from "@tCentro/Icons/CENErrorIcon";
import CustomApplicationState from "@tCentro/Store/CustomApplicationState";
import { COLORS } from "@tCentro/theme/design";
import React, { ChangeEvent, useEffect, useMemo, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends TextFieldProps {
    configurationCompleted?: boolean;
    configurationSelection?: SafeDictionary<string>;
    variantSelectionCompleted?: boolean;
    labelOverride?: React.ReactNode;
    extendedStyles?: TextFieldPresentationProps;
    onBtnclickQtyValue?: number | null;
    setBtnQty?: (qty: number) => void;
    setlMinErr?: (val: boolean) => void;
    setlMultiErr?: (val: boolean) => void;
    setGMinErr?: (val: boolean) => void;
    setCompareMinErr?: (val: boolean) => void;
    setGMultiErr?: (val: boolean) => void;
    setCMinErr?: (val: boolean) => void;
    setCMultiErr?: (val: boolean) => void;
    setMylistMinErr?: (val: boolean) => void;
    setMylistMultiErr?: (val: boolean) => void;
    setCompareMultiErr?: (val: boolean) => void;
    isProductCarousel?: boolean | null;
}

const mapStateToProps = (
    state: ApplicationState & CustomApplicationState,
    props: {
        configurationCompleted?: boolean;
        variantSelectionCompleted?: boolean;
        configurationSelection?: SafeDictionary<string>;
    } & HasProductContext,
) => {
    return {
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            props.configurationCompleted,
            !!props.configurationSelection,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
        mutipleSaleQty: props.productContext.product?.detail?.multipleSaleQty,
        roundingRule: props.productContext.product?.detail?.roundingRule,
        isMinErrorObj: state.t_components.isMinError,
        isMultiErrorObj: state.t_components.isMultiError,
    };
};

const mapDispatchToProps = {
    cenQtyMin,
    cenQtyMulti,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasProductContext;

export const productQuantityOrderedStyles: TextFieldPresentationProps = {
    cssOverrides: {
        inputSelect: css`
            -moz-appearance: textfield;
            border-radius: 40px;
            width: 104px;
            height: 32px;
            padding: 12px 16px;
            &:focus {
                border-radius: 40px;
            }
        `,
    },
};

const ProductQuantityOrdered = ({
    hasEnoughInventory,
    mutipleSaleQty,
    roundingRule,
    productContext: {
        product: { id: productId, isVariantParent },
        onQtyOrderedChanged,
        productInfo: { qtyOrdered, failedToLoadInventory },
    },
    labelOverride,
    extendedStyles,
    canAddToCart,
    configurationCompleted,
    variantSelectionCompleted,
    onBtnclickQtyValue = null,
    setBtnQty,
    isMinErrorObj,
    isMultiErrorObj,
    cenQtyMin,
    cenQtyMulti,
    setGMinErr,
    setCompareMinErr,
    setGMultiErr,
    setlMinErr,
    setlMultiErr,
    setCMinErr,
    setCMultiErr,
    setMylistMinErr,
    setMylistMultiErr,
    setCompareMultiErr,
    isProductCarousel = false,
    ...otherProps
}: Props) => {
    const [hasQtyError, setHasQtyError] = useState(false);
    const [styles, setStyles] = useState<TextFieldPresentationProps>(() =>
        mergeToNew(productQuantityOrderedStyles, extendedStyles),
    );
    useEffect(() => {
        setStyles(mergeToNew(productQuantityOrderedStyles, extendedStyles));
    }, [extendedStyles]);
    const [productQty, setProductQty] = useState(() => {
        if (onBtnclickQtyValue !== undefined && onBtnclickQtyValue !== null) {
            return onBtnclickQtyValue.toString();
        }

        if (mutipleSaleQty && mutipleSaleQty > 0 && roundingRule === "Multiple Only") {
            return mutipleSaleQty.toString();
        }

        return qtyOrdered.toString();
    });

    useEffect(() => {
        if (qtyOrdered === Number(productQty)) {
            return;
        }
        setProductQty(`${qtyOrdered || ""}`);
    }, [qtyOrdered]);

    useEffect(() => {
        if (onBtnclickQtyValue !== null) {
            qtyChangeHandler(onBtnclickQtyValue.toString());
        }
    }, [onBtnclickQtyValue]);

    if (!hasEnoughInventory && !failedToLoadInventory) {
        return null;
    }

    const keyPressHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.which === 13) {
            (event.target as any).blur();
        }
    };

    const qtyChangeHandler = (event: ChangeEvent<HTMLInputElement> | string) => {
        const value = typeof event === "string" ? event : event?.target?.value;

        if (mutipleSaleQty && mutipleSaleQty > 0 && roundingRule === "Multiple Only") {
            if (Number(value) < mutipleSaleQty) {
                setHasQtyError(true);
                if (!isProductCarousel) {
                    cenQtyMin({ isMinError: true });
                    cenQtyMulti({ isMultiError: false });
                    setHasQtyError(false);
                }
                setGMinErr && setGMinErr(true);
                setlMinErr && setlMinErr(true);
                isProductCarousel && setCMinErr && setCMinErr(true);
                setCompareMinErr && setCompareMinErr(true);
                setMylistMinErr && setMylistMinErr(true);
                setlMultiErr && setlMultiErr(false);
                setGMultiErr && setGMultiErr(false);
                isProductCarousel && setCMultiErr && setCMultiErr(false);
                setMylistMultiErr && setMylistMultiErr(false);
                setCompareMultiErr && setCompareMultiErr(false);
            } else if (Number(value) > mutipleSaleQty && Number(value) % mutipleSaleQty !== 0) {
                setHasQtyError(true);
                if (!isProductCarousel) {
                    cenQtyMin({ isMinError: false });
                    cenQtyMulti({ isMultiError: true });
                    setHasQtyError(false);
                }
                setGMinErr && setGMinErr(false);
                setlMinErr && setlMinErr(false);
                isProductCarousel && setCMinErr && setCMinErr(false);
                setMylistMinErr && setMylistMinErr(false);
                setCompareMinErr && setCompareMinErr(false);
                setlMultiErr && setlMultiErr(true);
                setGMultiErr && setGMultiErr(true);
                isProductCarousel && setCMultiErr && setCMultiErr(true);
                setMylistMultiErr && setMylistMultiErr(true);
                setCompareMultiErr && setCompareMultiErr(true);
            } else {
                setHasQtyError(false);
                if (!isProductCarousel) {
                    cenQtyMin({ isMinError: false });
                    cenQtyMulti({ isMultiError: false });
                    setHasQtyError(false);
                }
                setGMinErr && setGMinErr(false);
                setlMinErr && setlMinErr(false);
                isProductCarousel && setCMinErr && setCMinErr(false);
                setCompareMinErr && setCompareMinErr(false);
                setlMultiErr && setlMultiErr(false);
                setGMultiErr && setGMultiErr(false);
                isProductCarousel && setCMultiErr && setCMultiErr(false);
                setMylistMinErr && setMylistMinErr(false);

                setMylistMultiErr && setMylistMultiErr(false);
                setCompareMultiErr && setCompareMultiErr(false);
            }
        }

        setProductQty(value);
        setBtnQty?.(Number(value));

        if (!onQtyOrderedChanged) {
            logger.warn(`There was no onQtyOrderedChanged passed to the ProductContext for ${productId}`);
            return;
        }

        const possibleNumber = Number(value);
        if (!Number.isNaN(possibleNumber)) {
            onQtyOrderedChanged(possibleNumber);
        }
    };

    return (
        <>
            <TextField
                label={labelOverride ?? translate("QTY:")}
                value={productQty}
                type="number"
                min={0}
                max={999999}
                data-test-selector="product_qtyOrdered"
                {...otherProps}
                onKeyPress={keyPressHandler}
                onChange={qtyChangeHandler}
                {...styles}
                disabled={!variantSelectionCompleted && isVariantParent}
                css={
                    hasQtyError || ((isMinErrorObj?.isMinError || isMultiErrorObj?.isMultiError) && !isProductCarousel)
                        ? css`
                              border: 2px solid ${COLORS.alertMsgRed} !important;
                          `
                        : undefined
                }
            />
        </>
    );
};

export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductQuantityOrdered));
